import React, { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { IError } from "src/components/dialog/error";
import { Input, Button, ErrorDialog, Container } from "src/components";
import { useForm } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { localizationStrings } from "./constants";
import { Auth } from "aws-amplify";
import LocalText from "src/local";
import { useAccountIsNicknameExistAction } from "src/hooks";
import { useNavigation } from "@react-navigation/native";
import { RootStackScreenProps } from "src/navigation/types";

type FormValues = {
  nickname: string;
  email: string;
  password: string;
  passwordConfirm: string;
};

export const AuthSignUpScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"SignUp">["navigation"]>();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      nickname: "",
      email: "",
      password: "",
      passwordConfirm: "",
    },
  });
  const [error, setError] = useState<IError | null>(null);
  const [processed, setProcessed] = useState<boolean>(false);
  const [accountIsNicknameExistAction] = useAccountIsNicknameExistAction(true);
  const [isNameExist, setIsNameExist] = useState<boolean>(false);
  const [agree, setAgree] = useState<boolean>(false);

  useEffect(() => {
    register("nickname", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputNicknameValidationRequire;
        }
        return true;
      },
    });
    register("email", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputEmailValidationRequire;
        }
        if (!formValidators.EmailValidator(value)) {
          return localizationStrings.inputEmailValidationFormat;
        }
        return true;
      },
    });
    register("password", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputPasswordValidationRequire;
        }
        // TODO: Add password pattern
        return true;
      },
    });
    register("passwordConfirm", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputPasswordConfirmValidationRequire;
        }
        if (getValues("password") !== value) {
          return LocalText.screens.authSignup.inputPasswordValidationMismatch;
        }
        // TODO: Add password pattern
        return true;
      },
    });
  }, [register]);
  const onSignUp = async (data: FormValues) => {
    if (isNameExist) {
      return;
    }
    setProcessed(true);
    try {
      await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes: {
          nickname: data.nickname,
        },
      });
      setProcessed(false);
      navigation.navigate("SignUpConfirm", { email: data.email });
    } catch (e: any) {
      if (e.code === "UsernameExistsException") {
        const authError = new Error(
          LocalText.screens.authSignup.errorUsernameExistsException
        );
        console.error(authError);
        setError(authError);
      } else if (e.code === "InvalidParameterException") {
        const authError = new Error(
          LocalText.screens.authSignup.errorInvalidParameterException
        );
        console.error(authError);
        setError(authError);
      } else {
        console.error(e);
        setError(e as Error);
      }
      setProcessed(false);
    }
  };

  const onNicknameChange = useCallback(async () => {
    const name = getValues("nickname");
    if (name.length > 0) {
      const response = await accountIsNicknameExistAction({ nickname: name });
      setIsNameExist(response !== false);
    } else {
      setIsNameExist(false);
    }
  }, [getValues]);

  return (
    <Container loading={processed}>
      <ScrollView>
        <View style={styles.container}>
          <Input
            placeholder={localizationStrings.inputNicknamePlaceholder}
            name={"nickname"}
            control={control}
            autoCapitalize={"none"}
            error={
              errors.nickname?.message ||
              (isNameExist
                ? LocalText.screens.myProfile.nicknameValidationExist
                : undefined)
            }
            onBlur={() => onNicknameChange()}
          />
          <Input
            placeholder={localizationStrings.inputEmailPlaceholder}
            name={"email"}
            control={control}
            autoCapitalize={"none"}
            error={errors.email?.message}
            keyboardType={"email-address"}
          />
          <Input
            placeholder={localizationStrings.inputPasswordPlaceholder}
            name={"password"}
            control={control}
            error={errors.password?.message}
            secureTextEntry={true}
          />
          <Input
            placeholder={localizationStrings.inputPasswordConfirmPlaceholder}
            name={"passwordConfirm"}
            control={control}
            error={errors.passwordConfirm?.message}
            secureTextEntry={true}
          />
          <View style={styles.buttonContainer}>
            <Button
              title={"利用規約を確認する"}
              containerStyle={{ paddingVertical: 0 }}
              variant={"link"}
              onPress={() => navigation.navigate("Terms")}
            />
            <Button
              title={"プライバシーポリシーを確認する"}
              containerStyle={{ paddingVertical: 0 }}
              variant={"link"}
              onPress={() => navigation.navigate("PrivacyPolicy")}
            />
            <Button
              iconColor={agree ? "#FFFFFF" : "#FF3000"}
              variant={"primary"}
              icon={"check"}
              textStyle={{ fontSize: 12 }}
              containerStyle={{ paddingRight: 8 }}
              title={"利用規約とプライバシーポリシーを確認しました"}
              onPress={() => setAgree((v) => !v)}
            />
            <Button
              title={localizationStrings.btnSignUpSendCode}
              loading={processed}
              disabled={!agree}
              onPress={handleSubmit(onSignUp)}
            />
            <Button
              title={localizationStrings.btnSignUpCancel}
              variant={"link"}
              onPress={() => navigation.goBack()}
            />
          </View>
        </View>
      </ScrollView>
      {error ? (
        <ErrorDialog
          error={error}
          visible={true}
          onClose={() => setError(null)}
        />
      ) : null}
    </Container>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  buttonContainer: {
    flexDirection: "column",
    marginTop: 16,
    marginHorizontal: 16,
  },
});
